import React, { useState } from 'react'
import { Info, TrendingUp, TrendingDown, AlertTriangle, ExternalLink, BarChart3 } from 'lucide-react'
import { useLocation } from 'wouter'
import { useRevenueConcentration } from '../lib/data-service'
import { useFiscalWeekContext } from '../contexts/FiscalWeekContext'

interface TopRevenueConcentrationTileProps {
  className?: string
  topN?: number
}

export function TopRevenueConcentrationTile({ className = '', topN = 3 }: TopRevenueConcentrationTileProps) {
  const { selectedFiscalWeek } = useFiscalWeekContext()
  const { data: concentrationData, isLoading, error, refetch } = useRevenueConcentration(selectedFiscalWeek, topN)
  const [showTooltip, setShowTooltip] = useState(false)
  const [, setLocation] = useLocation()

  // Format percentage
  const formatPercent = (value: number | null, decimals = 1) => {
    if (value === null) return '—'
    return `${(value * 100).toFixed(decimals)}%`
  }

  // Format currency with compact notation
  const formatCurrency = (value: number | null, compact = true) => {
    if (value === null) return '—'
    if (compact) {
      if (Math.abs(value) >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`
      } else if (Math.abs(value) >= 1000) {
        return `$${(value / 1000).toFixed(1)}k`
      }
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  // Format delta (percentage points)
  const formatDeltaPP = (value: number | null) => {
    if (value === null) return '—'
    const sign = value >= 0 ? '+' : ''
    return `${sign}${(value * 100).toFixed(1)} pp`
  }

  // Get concentration risk level
  const getConcentrationRisk = (value: number | null) => {
    if (value === null) return null
    if (value >= 0.5) return 'High'
    if (value >= 0.35) return 'Moderate'
    return 'Low'
  }

  // Get risk color
  const getRiskColor = (risk: string | null) => {
    switch (risk) {
      case 'High': return 'text-red-600 dark:text-red-400'
      case 'Moderate': return 'text-yellow-600 dark:text-yellow-400'
      case 'Low': return 'text-green-600 dark:text-green-400'
      default: return 'text-muted-foreground'
    }
  }

  // Handle drill-through to SKUs page
  const handleDrillThrough = () => {
    setLocation(`/skus-detail?week=${selectedFiscalWeek}&sort=rev_desc&limit=50`)
  }

  // Loading skeleton
  if (isLoading) {
    return (
      <div className={`card-premium-elevated ${className}`}>
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-9 h-9 bg-gray-200 rounded-lg"></div>
              <div>
                <div className="h-5 bg-gray-200 rounded w-48 mb-2"></div>
              </div>
            </div>
          </div>
          <div className="flex justify-between items-start mb-4">
            <div className="h-12 bg-gray-200 rounded w-20"></div>
            <div className="h-8 bg-gray-200 rounded w-24"></div>
          </div>
          <div className="h-8 bg-gray-200 rounded w-full mb-6"></div>
          <div className="space-y-3">
            <div className="h-5 bg-gray-200 rounded w-full"></div>
            <div className="h-5 bg-gray-200 rounded w-full"></div>
            <div className="h-5 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className={`card-premium-elevated ${className}`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Revenue Concentration</h3>
          </div>
        </div>
        <p className="text-sm text-red-600 mb-4">Failed to load concentration data</p>
        <button 
          onClick={() => refetch()}
          className="text-sm text-blue-600 hover:text-blue-700 underline font-medium"
        >
          Retry
        </button>
      </div>
    )
  }

  // Empty state
  if (!concentrationData || concentrationData.total_revenue === 0) {
    return (
      <div className={`card-premium-elevated ${className}`}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <BarChart3 className="w-5 h-5 text-gray-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Revenue Concentration</h3>
          </div>
        </div>
        <p className="text-4xl font-bold text-gray-400 mb-2">—</p>
        <p className="text-sm text-gray-500">No revenue this week</p>
      </div>
    )
  }

  const concentrationPercent = concentrationData.concentration_percent ?? 0
  const concentration = concentrationPercent / 100
  const delta = null
  const risk = getConcentrationRisk(concentration)
  const products = concentrationData.products ?? []

    return (
      <div className={`card-premium-elevated ${className}`}>
      {/* Header with Info Icon */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <BarChart3 className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold text-gray-900 cursor-pointer hover:text-purple-600 transition-colors" onClick={handleDrillThrough}>
              Revenue Concentration
            </h3>
            <ExternalLink className="w-4 h-4 text-gray-400" />
          </div>
        </div>
        <div className="relative">
          <button
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
            onClick={() => setShowTooltip(!showTooltip)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Info className="w-5 h-5" />
          </button>
          
          {/* Tooltip */}
          {showTooltip && (
            <div className="absolute top-6 right-0 w-80 p-4 bg-popover border border-border rounded-lg shadow-xl z-50 text-xs">
              <h4 className="font-semibold text-popover-foreground mb-2">How we compute Top-{topN} Revenue Concentration</h4>
              <div className="space-y-2 text-popover-foreground">
                <p>• We rank SKUs by TY revenue and sum the top {topN}</p>
                <p>• Concentration % = Top-{topN} revenue ÷ Total revenue</p>
                {products.length < topN && (
                  <p className="text-yellow-400">
                    ⚠︎ Only {products.length} SKUs available this week
                  </p>
                )}
              </div>
              <div className="mt-3 pt-2 border-t border-border text-popover-foreground">
                <p><strong>Totals:</strong></p>
                <p>• Top-{topN}: {formatCurrency(concentrationData.top_n_revenue, false)}</p>
                <p>• Total: {formatCurrency(concentrationData.total_revenue, false)}</p>
                <p>• Share: {formatPercent(concentration)}</p>
              </div>
              <p className="text-muted-foreground mt-3 pt-2 border-t border-border">
                Higher concentration increases risk (dependency on few SKUs). Track trend over time.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Primary Display */}
      <div className="flex justify-between items-start mb-6">
        {/* Big Numeral */}
        <div>
          <p className="text-4xl font-bold text-gray-900">
            {formatPercent(concentration)}
          </p>
        </div>

        {/* Risk Badge */}
        {risk && (
          <div className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
            risk === 'High' ? 'bg-red-50 text-red-700 border-red-200' :
            risk === 'Moderate' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
            'bg-green-50 text-green-700 border-green-200'
          }`}>
            {risk} Risk
          </div>
        )}
      </div>

      {/* Delta Chip */}
      {delta !== null && (
        <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium mb-8 ${
          delta >= 0 
            ? 'bg-green-50 text-green-700 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {delta >= 0 ? (
            <TrendingUp className="w-4 h-4" />
          ) : (
            <TrendingDown className="w-4 h-4" />
          )}
          <span>Δ vs LY {formatDeltaPP(delta)}</span>
        </div>
      )}

      {/* Stacked 100% Bar */}
      <div className="mb-8">
        <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden flex data-visualization">
          {/* Top-N Segment */}
          <div 
            className="bg-gradient-to-r from-purple-500 to-purple-600 flex items-center justify-center text-white text-sm font-medium transition-all duration-300"
            style={{ width: `${concentration ? concentration * 100 : 0}%` }}
          >
            {concentration && concentration > 0.3 && `Top-${topN}`}
          </div>
          {/* All Other Segment */}
          <div 
            className="bg-gradient-to-r from-gray-400 to-gray-500 flex items-center justify-center text-white text-sm font-medium transition-all duration-300"
            style={{ width: `${concentration ? (1 - concentration) * 100 : 100}%` }}
          >
            {concentration && (1 - concentration) > 0.3 && 'Others'}
          </div>
        </div>
        <div className="flex justify-between text-sm text-gray-600 mt-2 font-medium">
          <span>Top-{topN} SKUs</span>
          <span>All Other</span>
        </div>
      </div>

      {/* Top SKUs Table */}
      <div className="space-y-4">
        {products.length > 0 ? (
          <>
            {products.map((product, index) => (
              <div key={product.sku ?? index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <span className="text-sm bg-purple-100 text-purple-700 px-3 py-1 rounded-full font-medium">#{index + 1}</span>
                    <span className="text-sm text-gray-900 font-medium" title={product.name || product.sku}>
                      {(product.name || product.sku || '').slice(0, 25)}
                    </span>
                  </div>
                </div>
                <div className="text-right ml-3">
                  <div className="text-sm font-semibold text-gray-900">{formatCurrency(product.revenue)}</div>
                  <div className="text-xs text-gray-600">{formatPercent((product.percent_of_total ?? 0) / 100, 0)}</div>
                </div>
              </div>
            ))}
            
            {/* View Details Link */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={handleDrillThrough}
                className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-700 transition-colors font-medium"
              >
                <span>View all SKUs</span>
                <ExternalLink className="w-4 h-4" />
              </button>
            </div>
          </>
        ) : (
          /* No SKU data available */
          <div className="text-center py-8">
            <div className="text-gray-400 mb-2">
              <BarChart3 className="w-12 h-12 mx-auto" />
            </div>
            <p className="text-sm text-gray-600 mb-2">No SKU-level data available</p>
            <p className="text-xs text-gray-500">
              Revenue concentration requires detailed SKU data which is not available in the current dataset.
            </p>
          </div>
        )}
      </div>

      {/* Debug Info (development only) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 pt-2 border-t border-border text-xs text-muted-foreground">
          Week: {concentrationData.week} | Top-{topN}: {formatCurrency(concentrationData.topN_sum_ty, false)} | Total: {formatCurrency(concentrationData.total_ty, false)} | Risk: {risk}
        </div>
      )}
    </div>
  )
}

export default TopRevenueConcentrationTile
